export interface HqFeatureReadinessInput {
  dev: boolean
  reportAnalysisApiReady: boolean
  paymentAnalysisApiReady: boolean
  marketingEffectApiReady: boolean
  referralCashbackApiReady?: boolean
  referralCashbackTestApi?: boolean
  referralCashbackReview?: boolean
  customReportsApiReady: boolean
  memberRiskAnalyticsApiReady: boolean
  memberRiskRuleConfigApiReady: boolean
  memberTagApiReady: boolean
}

export interface HqFeatureReadiness {
  previewEnabled: boolean
  comingSoonRegistered: boolean
  reservationCenterRegistered: boolean
  reservationRulesRegistered: boolean
  dataScreenRegistered: boolean
  reportAnalysisRegistered: boolean
  reportAnalysisUsePreview: boolean
  paymentAnalysisApiReady: boolean
  paymentAnalysisRegistered: boolean
  paymentAnalysisUsePreview: boolean
  marketingEffectRegistered: boolean
  marketingEffectUsePreview: boolean
  referralCashbackRegistered: boolean
  referralCashbackUsePreview: boolean
  referralCashbackTestApiMode: boolean
  referralCashbackReviewMode: boolean
  customReportsApiReady: boolean
  customReportsRegistered: boolean
  memberRiskAnalyticsApiReady: boolean
  memberRiskRuleConfigApiReady: boolean
  memberTagApiReady: boolean
}

/**
 * 总部功能就绪状态的唯一入口。
 *
 * 尚未交付的接口只有在开关明确就绪后才允许生产注册；仅明确保留预览能力的旧页面可在开发服务器进入只读演示。
 * 已交付的收支管理不再属于 readiness 范畴，始终注册并由权限与版本路由控制访问。
 * 收款分析和自定义报表没有本地样例兜底，即使在开发环境也默认 fail-closed。
 * 纯占位/纯演示页默认只在开发服务器注册，避免生产直达。
 * 总部预订中心已接入真实只读接口，按 PRD 常驻注册，访问仍受组织范围和权限控制。
 *
 * member-risk 两个开关是完整业务语义门禁，不是“端点已存在”门禁：
 * 必须在真实 producer、聚合/筛选口径和规则消费者全部 ready 后才能开启，DEV 也不自动绕过。
 */
export function resolveHqFeatureReadiness(input: HqFeatureReadinessInput): HqFeatureReadiness {
  const previewEnabled = input.dev

  return {
    previewEnabled,
    comingSoonRegistered: previewEnabled,
    reservationCenterRegistered: true,
    reservationRulesRegistered: false,
    dataScreenRegistered: previewEnabled,
    reportAnalysisRegistered: input.reportAnalysisApiReady || previewEnabled,
    reportAnalysisUsePreview: previewEnabled && !input.reportAnalysisApiReady,
    paymentAnalysisApiReady: input.paymentAnalysisApiReady,
    paymentAnalysisRegistered: input.paymentAnalysisApiReady,
    paymentAnalysisUsePreview: false,
    marketingEffectRegistered: input.marketingEffectApiReady || previewEnabled,
    marketingEffectUsePreview: previewEnabled && !input.marketingEffectApiReady,
    referralCashbackRegistered: Boolean(input.referralCashbackApiReady)
      || Boolean(input.referralCashbackTestApi)
      || Boolean(input.referralCashbackReview)
      || previewEnabled,
    referralCashbackUsePreview: Boolean(input.referralCashbackReview)
      || (previewEnabled
        && !input.referralCashbackApiReady
        && !input.referralCashbackTestApi),
    referralCashbackTestApiMode: Boolean(input.referralCashbackTestApi),
    referralCashbackReviewMode: Boolean(input.referralCashbackReview),
    customReportsApiReady: input.customReportsApiReady,
    customReportsRegistered: input.customReportsApiReady,
    memberRiskAnalyticsApiReady: input.memberRiskAnalyticsApiReady,
    memberRiskRuleConfigApiReady: input.memberRiskRuleConfigApiReady,
    memberTagApiReady: input.memberTagApiReady,
  }
}

export const HQ_FEATURE_READINESS = resolveHqFeatureReadiness({
  dev: import.meta.env.DEV,
  reportAnalysisApiReady: import.meta.env.VITE_HQ_REPORT_ANALYSIS_API_READY === "true",
  paymentAnalysisApiReady: import.meta.env.VITE_HQ_PAYMENT_ANALYSIS_API_READY === "true",
  marketingEffectApiReady: import.meta.env.VITE_HQ_MARKETING_EFFECT_API_READY === "true",
  // 本版已交付管理接口，品牌参与资格由2S维护；管理入口仍受总部权限控制。
  referralCashbackApiReady: true,
  referralCashbackTestApi: import.meta.env.VITE_HQ_REFERRAL_CASHBACK_TEST_API === "true",
  referralCashbackReview: import.meta.env.DEV && (import.meta.env.MODE === "referral-review" || import.meta.env.VITE_HQ_REFERRAL_CASHBACK_REVIEW === "true"),
  customReportsApiReady: import.meta.env.VITE_HQ_CUSTOM_REPORT_API_READY === "true",
  memberRiskAnalyticsApiReady: import.meta.env.VITE_MEMBER_RISK_ANALYTICS_API_READY === "true",
  memberRiskRuleConfigApiReady: import.meta.env.VITE_MEMBER_RISK_RULE_CONFIG_API_READY === "true",
  memberTagApiReady: import.meta.env.VITE_MEMBER_TAG_API_READY === "true",
})
